/**
 * PBDeltaCard — Best vs Latest auto-compare card.
 *
 * Extracted from AthleteDashboard.tsx (Feature 5 / Phase D).
 * Same interface and behavior as the inline BestVsLatestCard; pure mechanical extraction.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { formatDate } from '@/utils/formatters';
import type { EvaluationSummaryItem } from '@/services/evaluationApi';

export interface PBDeltaCardProps {
  pbEval: EvaluationSummaryItem;
  latestEval: EvaluationSummaryItem;
  isLatestThePB: boolean;
}

export function PBDeltaCard({ pbEval, latestEval, isLatestThePB }: PBDeltaCardProps) {
  if (isLatestThePB) {
    return (
      <Card>
        <CardHeader style={{ paddingBottom: '8px' }}>
          <CardTitle>Best vs Latest</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              padding: '16px',
              backgroundColor: 'rgba(244, 201, 93, 0.08)',
              border: '1px solid rgba(244, 201, 93, 0.22)',
              borderRadius: '12px',
            }}
          >
            <span style={{ fontSize: '28px' }}>🏆</span>
            <div>
              <p
                style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '15px',
                  color: 'var(--color-accent-gold)',
                  margin: 0,
                }}
              >
                New Personal Best!
              </p>
              <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', margin: '3px 0 0' }}>
                Your latest session ({Math.round(latestEval.overall_score ?? 0)}) is your all-time best.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Build a phase comparison if both evals have phase data
  const pbPhases = pbEval.phase_scores ?? [];
  const latestPhases = latestEval.phase_scores ?? [];
  const hasPhaseData = pbPhases.length > 0 && latestPhases.length > 0;

  const phaseNames = hasPhaseData
    ? pbPhases.map(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (p: any) => p.phase_name ?? p.phase_label ?? `Phase ${p.phase_index ?? ''}`
      )
    : [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getPhaseScore = (phases: any[], name: string): number | undefined => {
     
    const match = phases.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (p: any) => (p.phase_name ?? p.phase_label ?? `Phase ${p.phase_index ?? ''}`) === name
    );
    return match?.form_score ?? match?.score ?? match?.overall_phase_score;
  };

  const pbScore = Math.round(pbEval.overall_score ?? 0);
  const latestScore = Math.round(latestEval.overall_score ?? 0);
  const overallDelta = latestScore - pbScore;

  return (
    <Card>
      <CardHeader style={{ paddingBottom: '8px' }}>
        <CardTitle>Best vs Latest</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Overall row */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: '8px',
            marginBottom: hasPhaseData ? '16px' : '0',
            padding: '10px 0',
            borderBottom: hasPhaseData ? '1px solid var(--color-border-subtle)' : 'none',
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              PB · {formatDate(pbEval.created_at, 'MMM d')}
            </p>
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '28px',
                color: 'var(--color-accent-gold)',
              }}
            >
              {pbScore}
            </span>
          </div>
          <div style={{ textAlign: 'center', padding: '0 4px' }}>
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '13px',
                color: overallDelta >= 0 ? 'var(--color-accent)' : 'var(--color-accent-red)',
                display: 'block',
              }}
            >
              {overallDelta >= 0 ? `+${overallDelta}` : overallDelta}
            </span>
            <span style={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>Overall</span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
              Latest · {formatDate(latestEval.created_at, 'MMM d')}
            </p>
            <span
              style={{
                fontFamily: 'var(--font-heading)',
                fontWeight: 700,
                fontSize: '28px',
                color: 'var(--color-text-primary)',
              }}
            >
              {latestScore}
            </span>
          </div>
        </div>

        {/* Per-phase rows */}
        {hasPhaseData && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {phaseNames.map((name: string) => {
              const pb = getPhaseScore(pbPhases, name);
              const latest = getPhaseScore(latestPhases, name);
              if (pb === undefined && latest === undefined) return null;
              const delta =
                pb !== undefined && latest !== undefined
                  ? Math.round(latest) - Math.round(pb)
                  : null;
              const deltaColor =
                delta === null
                  ? 'var(--color-text-muted)'
                  : delta > 0
                  ? 'var(--color-accent)'
                  : delta < 0
                  ? 'var(--color-accent-red)'
                  : 'var(--color-text-muted)';
              return (
                <div
                  key={name}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr auto',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                  }}
                >
                  <span
                    style={{
                      color: 'var(--color-text-muted)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={name}
                  >
                    {name}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      color: 'var(--color-accent-gold)',
                      minWidth: '28px',
                      textAlign: 'right',
                    }}
                  >
                    {pb !== undefined ? Math.round(pb) : '—'}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      minWidth: '28px',
                      textAlign: 'right',
                    }}
                  >
                    {latest !== undefined ? Math.round(latest) : '—'}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 600,
                      fontSize: '11px',
                      color: deltaColor,
                      minWidth: '32px',
                      textAlign: 'right',
                    }}
                  >
                    {delta !== null
                      ? delta > 0
                        ? `+${delta}`
                        : delta === 0
                        ? '—'
                        : `${delta}`
                      : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {!hasPhaseData && (
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
            Phase detail unavailable — evaluations without phase data show overall score only.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default PBDeltaCard;
